import { useCoreServices } from '@bangle.io/context';
import { normalizeWordsPerPage } from '@bangle.io/service-core';
import { Sidebar, AppSidebar as UIAppSidebar } from '@bangle.io/ui-components';
import { WsDirPath, WsPath } from '@bangle.io/ws-path';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React from 'react';
import { usePwaInstall } from '../common/use-pwa-install';
import { getWorkspaceStorageSourceLabel } from '../common/workspace-storage-source-label';
import { useCloudWorkspaceAccess } from '../share/use-cloud-workspace-access';
import {
  useMarkPathPrivate,
  usePrivatePaths,
} from '../share/use-private-paths';
import { SidebarFooterMenu } from './sidebar-footer-menu';
import { useSidebarFileActions } from './use-sidebar-file-actions';
import { WorkspaceStatsStripContainer } from './workspace-stats-strip-container';

interface SidebarProps {
  children: React.ReactNode;
}

export const AppSidebar = ({ children }: SidebarProps) => {
  const {
    commandDispatcher,
    workspaceState,
    workbenchState,
    navigation,
    userActivityService,
  } = useCoreServices();
  const setOpenOmniSearch = useSetAtom(workbenchState.$openOmniSearch);
  const workspaces = useAtomValue(workspaceState.$workspaces);
  const [sidebarOpen, setSidebarOpen] = useAtom(workbenchState.$sidebarOpen);
  const [sidebarWidth, setSidebarWidth] = useAtom(workbenchState.$sidebarWidth);
  const fileTreeExpandedPathsByWorkspace = useAtomValue(
    workbenchState.$fileTreeExpandedPathsByWorkspace,
  );
  const activeWsName = useAtomValue(workspaceState.$currentWsName);
  const { workspaceId, isViewer, isOwner } = useCloudWorkspaceAccess();
  const privatePaths = usePrivatePaths(workspaceId);
  const markPathPrivate = useMarkPathPrivate();
  const activeWsPaths = useAtomValue(workspaceState.$activeWsPaths);
  const wsPaths = useAtomValue(workspaceState.$wsPaths);
  const noteWsPaths = useAtomValue(workspaceState.$noteWsPaths);
  const noteContentStats = useAtomValue(workspaceState.$noteContentStats);
  const starredWsPaths = useAtomValue(userActivityService.$starredWsPaths);
  const fileTreeListState = useAtomValue(workspaceState.$fileTreeListState);
  const activeWorkspace = React.useMemo(
    () => workspaces.find((ws) => ws.name === activeWsName),
    [activeWsName, workspaces],
  );
  const wordsPerPage = React.useMemo(
    () => normalizeWordsPerPage(activeWorkspace?.metadata.wordsPerPage),
    [activeWorkspace],
  );

  // Keep this domain-to-view join local until another consumer needs it.
  const starredItems = React.useMemo(() => {
    const notesByWsPath = new Map(
      noteWsPaths.map((wsPath) => [wsPath.wsPath, wsPath]),
    );
    const activePathSet = new Set(activeWsPaths.map((wsPath) => wsPath.wsPath));

    return starredWsPaths.flatMap((starredWsPath) => {
      const wsPath = notesByWsPath.get(starredWsPath);
      return wsPath
        ? [
            {
              title: wsPath.fileName || wsPath.path,
              wsPath: wsPath.wsPath,
              isActive: activePathSet.has(wsPath.wsPath),
            },
          ]
        : [];
    });
  }, [activeWsPaths, noteWsPaths, starredWsPaths]);

  const getActionsForEntry = useSidebarFileActions({
    activeWsName,
    readOnly: isViewer,
    commandDispatcher,
    cloudWorkspaceId: workspaceId,
    canManageNotePrivacy: isOwner,
    markPathPrivate,
    notePaths: noteWsPaths,
    contentStats: noteContentStats,
    wordsPerPage,
  });

  const pwaInstall = usePwaInstall();
  const pwaAction = React.useMemo(() => {
    if (pwaInstall.canInstall || pwaInstall.isInstalling) {
      return {
        kind: 'install' as const,
        label: pwaInstall.isInstalling
          ? t.app.sidebar.installingApp
          : t.app.sidebar.installApp,
        disabled: pwaInstall.isInstalling,
        onClick: () => {
          void pwaInstall.install();
        },
      };
    }

    if (pwaInstall.canOpenInApp) {
      return {
        kind: 'open-in-app' as const,
        label: t.app.sidebar.openInApp,
        onClick: () => {
          pwaInstall.openInApp();
        },
      };
    }

    return undefined;
  }, [pwaInstall]);

  const handleFileTreeDirectoryExpansionChange = React.useCallback(
    (path: string, expanded: boolean) => {
      if (activeWsName) {
        workbenchState.setFileTreeDirectoryExpanded(
          activeWsName,
          path,
          expanded,
        );
      }
    },
    [activeWsName, workbenchState],
  );
  const handleRevealFileTreePaths = React.useCallback(
    (paths: readonly string[]) => {
      if (activeWsName) {
        workbenchState.revealFileTreePaths(activeWsName, paths);
      }
    },
    [activeWsName, workbenchState],
  );
  const handleCollapseFileTree = React.useCallback(
    (keepExpandedPaths: readonly string[]) => {
      if (activeWsName) {
        workbenchState.collapseFileTree(activeWsName, keepExpandedPaths);
      }
    },
    [activeWsName, workbenchState],
  );

  return (
    <Sidebar.SidebarProvider
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      width={sidebarWidth}
      onWidthChange={setSidebarWidth}
    >
      <UIAppSidebar
        workspaces={workspaces.map((ws, _i) => ({
          name: ws.name,
          misc: getWorkspaceStorageSourceLabel(ws.type),
          isActive: activeWsName === ws.name,
        }))}
        filePaths={React.useMemo(
          () => wsPaths.map((wsPath) => wsPath.path),
          [wsPaths],
        )}
        privatePaths={privatePaths}
        navItems={activeWsPaths.map((wsPath) => ({
          title: wsPath.fileName || '',
          wsPath: wsPath.wsPath,
        }))}
        starredItems={starredItems}
        wsPathToHref={(wsPath) => navigation.toWsFileUri(wsPath)}
        wsNameToHref={(wsName) =>
          navigation.toUri({
            route: 'ws-home',
            payload: { wsName },
          })
        }
        canCreateFiles={Boolean(activeWsName) && !isViewer}
        onCreateDirectory={(pathPrefix) => {
          if (isViewer) {
            return;
          }
          if (!activeWsName) {
            return;
          }
          commandDispatcher.dispatch(
            'command::ui:create-directory-dialog',
            {
              pathPrefix,
            },
            'ui',
          );
        }}
        onCreateNote={(pathPrefix) => {
          if (isViewer) {
            return;
          }
          if (!activeWsName) {
            return;
          }
          commandDispatcher.dispatch(
            'command::ws:quick-new-note',
            {
              pathPrefix,
            },
            'ui',
          );
        }}
        onMoveFile={(sourceRelativePath, destinationDirectory) => {
          if (!activeWsName) {
            return;
          }

          const isExistingFile = wsPaths.some(
            (wsPath) => wsPath.path === sourceRelativePath,
          );

          if (isExistingFile) {
            commandDispatcher.dispatch(
              'command::ws:move-ws-path',
              {
                destDirWsPath: destinationDirectory
                  ? WsDirPath.fromParts(activeWsName, destinationDirectory)
                      .wsPath
                  : `${activeWsName}:`,
                wsPath: WsPath.fromParts(activeWsName, sourceRelativePath)
                  .wsPath,
              },
              'ui',
            );
            return;
          }

          const folderName = sourceRelativePath.includes('/')
            ? sourceRelativePath.slice(sourceRelativePath.lastIndexOf('/') + 1)
            : sourceRelativePath;
          const newDirPath = destinationDirectory
            ? WsPath.pathJoin(destinationDirectory, folderName)
            : folderName;

          commandDispatcher.dispatch(
            'command::ws:rename-directory',
            {
              oldDirWsPath: WsDirPath.fromParts(
                activeWsName,
                sourceRelativePath,
              ).wsPath,
              newDirWsPath: WsDirPath.fromParts(activeWsName, newDirPath)
                .wsPath,
            },
            'ui',
          );
        }}
        onOpenFile={(relativePath) => {
          if (!activeWsName) {
            return;
          }

          commandDispatcher.dispatch(
            'command::ws:go-ws-path',
            {
              wsPath: WsPath.fromParts(activeWsName, relativePath).wsPath,
            },
            'ui',
          );
        }}
        onNewWorkspaceClick={() => {
          commandDispatcher.dispatch(
            'command::ui:create-workspace-dialog',
            null,
            'ui',
          );
        }}
        onManageWorkspacesClick={() => {
          commandDispatcher.dispatch(
            'command::ui:open-settings-workspaces',
            null,
            'ui',
          );
        }}
        fileTreeNotice={
          fileTreeListState.status === 'error'
            ? {
                message: t.app.components.appSidebar.fileTreeErrorMessage,
                retryLabel: t.app.components.appSidebar.fileTreeErrorRetry,
                onRetry: () => {
                  commandDispatcher.dispatch(
                    'command::ws:refresh-file-tree',
                    null,
                    'ui',
                  );
                },
              }
            : undefined
        }
        activeFilePaths={activeWsPaths.map((wsPath) => wsPath.path)}
        expandedFileTreePaths={
          activeWsName &&
          Object.hasOwn(fileTreeExpandedPathsByWorkspace, activeWsName)
            ? fileTreeExpandedPathsByWorkspace[activeWsName]
            : undefined
        }
        onFileTreeDirectoryExpansionChange={
          handleFileTreeDirectoryExpansionChange
        }
        onRevealFileTreePaths={handleRevealFileTreePaths}
        onCollapseFileTree={handleCollapseFileTree}
        commandButtonClassName="desktop-titlebar-no-drag"
        onSearchClick={() => {
          setOpenOmniSearch(true);
        }}
        sidebarHeaderClassName="desktop-sidebar-titlebar-header desktop-titlebar-drag"
        workspaceSwitcherWrapperClassName="desktop-titlebar-no-drag"
        getActionsForEntry={getActionsForEntry}
        pwaAction={pwaAction}
        statsStrip={activeWsName ? <WorkspaceStatsStripContainer /> : undefined}
        footerTitle={t.app.sidebar.footerTitle}
        footerSubtitle={t.app.common.earlyAlpha}
        footerChildren={
          <SidebarFooterMenu
            canCreateFiles={Boolean(activeWsName) && !isViewer}
          />
        }
      />
      <Sidebar.SidebarInset>{children}</Sidebar.SidebarInset>
    </Sidebar.SidebarProvider>
  );
};
